/**
 * Shared nested column-group helpers for Columns pickers.
 * Used by domain ranking tables and Productivity (Missed Opportunity).
 */
import type { ColumnPrefs } from '../../hooks/useReportingColumnPrefs';

export type NestedColumnChildDef = {
    key: string;
    label: string;
};

export type NestedColumnGroupDef = {
    id: string;
    label: string;
    children: NestedColumnChildDef[];
    /** When parent is turned on from empty, these children become visible. */
    defaultChildKeys: string[];
};

export type NestedColumnDrawerChild = {
    key: string;
    label: string;
    visible: boolean;
};

export type NestedColumnDrawerRow = {
    key: string;
    label: string;
    visible: boolean;
    pinned: boolean;
    children?: NestedColumnDrawerChild[];
};

export function nestedParentLabel(baseLabel: string, children: NestedColumnDrawerChild[]): string {
    const labels = children
        .filter((c) => c.visible)
        .map((c) => c.label.replace(/\.$/, ''));
    if (labels.length === 0) return baseLabel;
    return `${baseLabel} (${labels.join(', ')})`;
}

function indexMaps(groups: NestedColumnGroupDef[]) {
    const byId = new Map(groups.map((g) => [g.id, g]));
    const byChild = new Map<string, NestedColumnGroupDef>();
    groups.forEach((g) => g.children.forEach((c) => byChild.set(c.key, g)));
    return { byId, byChild };
}

export function prefsToNestedRows(
    prefs: ColumnPrefs,
    allKeys: string[],
    labels: Record<string, string>,
    groups: NestedColumnGroupDef[],
    pinnedKeys: string[] = [],
): NestedColumnDrawerRow[] {
    const { byChild } = indexMaps(groups);
    const hidden = new Set(prefs.hidden);
    const pinned = new Set(pinnedKeys);
    const emittedGroups = new Set<string>();
    const rows: NestedColumnDrawerRow[] = [];

    const childOrderIndex = (key: string) => {
        const i = prefs.order.indexOf(key);
        return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };

    for (const key of prefs.order.filter((k) => allKeys.includes(k))) {
        const group = byChild.get(key);
        if (!group) {
            rows.push({
                key,
                label: labels[key] || key,
                visible: !hidden.has(key),
                pinned: pinned.has(key),
            });
            continue;
        }
        if (emittedGroups.has(group.id)) continue;
        emittedGroups.add(group.id);
        const children = [...group.children]
            .sort((a, b) => childOrderIndex(a.key) - childOrderIndex(b.key))
            .map((c) => ({
                key: c.key,
                label: c.label,
                visible: !hidden.has(c.key) && prefs.order.includes(c.key),
            }));
        rows.push({
            key: group.id,
            label: nestedParentLabel(group.label, children),
            visible: children.some((c) => c.visible),
            pinned: false,
            children,
        });
    }

    for (const key of allKeys) {
        const group = byChild.get(key);
        if (group) {
            if (emittedGroups.has(group.id)) continue;
            emittedGroups.add(group.id);
            const children = group.children.map((c) => ({
                key: c.key,
                label: c.label,
                visible: false,
            }));
            rows.push({
                key: group.id,
                label: group.label,
                visible: false,
                pinned: false,
                children,
            });
        } else if (!prefs.order.includes(key)) {
            rows.push({
                key,
                label: labels[key] || key,
                visible: false,
                pinned: pinned.has(key),
            });
        }
    }

    return rows;
}

export function nestedRowsToPrefs(
    rows: NestedColumnDrawerRow[],
    allKeys: string[],
    groups: NestedColumnGroupDef[] = [],
): ColumnPrefs {
    const groupIds = new Set(groups.map((g) => g.id));
    const order: string[] = [];
    const hidden: string[] = [];
    const seen = new Set<string>();

    for (const row of rows) {
        if (row.children && row.children.length > 0) {
            for (const child of row.children) {
                if (seen.has(child.key)) continue;
                order.push(child.key);
                seen.add(child.key);
                if (!child.visible) hidden.push(child.key);
            }
            continue;
        }
        if (seen.has(row.key) || groupIds.has(row.key)) continue;
        order.push(row.key);
        seen.add(row.key);
        if (!row.visible && !row.pinned) hidden.push(row.key);
    }

    for (const key of allKeys) {
        if (seen.has(key)) continue;
        order.push(key);
        hidden.push(key);
    }

    return { order, hidden };
}

export function toggleNestedRow(
    rows: NestedColumnDrawerRow[],
    key: string,
    groups: NestedColumnGroupDef[],
): NestedColumnDrawerRow[] {
    const { byId } = indexMaps(groups);

    return rows.map((row) => {
        if (row.key === key && row.children) {
            const anyOn = row.children.some((c) => c.visible);
            const group = byId.get(row.key);
            if (anyOn) {
                const children = row.children.map((c) => ({ ...c, visible: false }));
                return {
                    ...row,
                    visible: false,
                    label: group?.label ?? row.label,
                    children,
                };
            }
            const defaults = new Set(group?.defaultChildKeys ?? []);
            const children = row.children.map((c) => ({
                ...c,
                visible: defaults.size > 0 ? defaults.has(c.key) : c.key === row.children![0]?.key,
            }));
            return {
                ...row,
                visible: children.some((c) => c.visible),
                label: nestedParentLabel(group?.label ?? row.label, children),
                children,
            };
        }
        if (row.key === key && !row.children) {
            if (row.pinned) return row;
            return { ...row, visible: !row.visible };
        }
        if (row.children?.some((c) => c.key === key)) {
            const children = row.children.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c));
            const group = byId.get(row.key);
            return {
                ...row,
                visible: children.some((c) => c.visible),
                label: nestedParentLabel(group?.label ?? row.label, children),
                children,
            };
        }
        return row;
    });
}

export function reorderNestedRows(
    rows: NestedColumnDrawerRow[],
    fromKey: string,
    toKey: string,
): NestedColumnDrawerRow[] {
    if (fromKey === toKey) return rows;

    const parentOfChild = (key: string) => rows.find((r) => r.children?.some((c) => c.key === key));
    const fromParent = parentOfChild(fromKey);
    const toParent = parentOfChild(toKey);

    if (fromParent && toParent && fromParent.key === toParent.key && fromParent.children) {
        const children = [...fromParent.children];
        const fi = children.findIndex((c) => c.key === fromKey);
        const ti = children.findIndex((c) => c.key === toKey);
        if (fi < 0 || ti < 0) return rows;
        const [item] = children.splice(fi, 1);
        children.splice(ti, 0, item);
        return rows.map((r) => (r.key === fromParent.key ? { ...r, children } : r));
    }

    if (fromParent || toParent) return rows;

    const fromIdx = rows.findIndex((r) => r.key === fromKey);
    const toIdx = rows.findIndex((r) => r.key === toKey);
    if (fromIdx < 0 || toIdx < 0) return rows;
    if (rows[fromIdx].pinned || rows[toIdx].pinned) return rows;
    const next = [...rows];
    const [item] = next.splice(fromIdx, 1);
    const newToIdx = next.findIndex((r) => r.key === toKey);
    if (newToIdx < 0) return rows;
    next.splice(newToIdx, 0, item);
    return next;
}

export function visibleLeafKeysFromNestedRows(rows: NestedColumnDrawerRow[]): string[] {
    const keys: string[] = [];
    for (const row of rows) {
        if (row.children) {
            for (const child of row.children) {
                if (child.visible) keys.push(child.key);
            }
        } else if (row.visible) {
            keys.push(row.key);
        }
    }
    return keys;
}

export function cloneNestedRows(rows: NestedColumnDrawerRow[]): NestedColumnDrawerRow[] {
    return rows.map((r) => ({
        ...r,
        children: r.children?.map((c) => ({ ...c })),
    }));
}
